import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import {
  VehicleBookingDialog,
  type VehicleBookingDefaults,
} from "@/components/vehicles/VehicleBookingDialog";
import { VehicleCalendarGrid } from "@/components/vehicles/VehicleCalendarGrid";
import { useVehicles, type VehicleBooking } from "@/hooks/useVehicles";

/** Vehicle bookings in the same month-column layout as the project calendar. */
const VehiclePlanningPage = () => {
  const { data: vehicles, isLoading } = useVehicles();
  const [dialog, setDialog] = useState<{
    open: boolean;
    booking: VehicleBooking | null;
    defaults?: VehicleBookingDefaults;
  }>({ open: false, booking: null });

  return (
    <div className="h-full flex flex-col animate-fade-in">
      <PageHeader title="Vehicle Planning" />
      {isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-8 w-full" />
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : (
        <VehicleCalendarGrid
          vehicles={vehicles}
          onBookingClick={(booking) => setDialog({ open: true, booking })}
          onCreateBooking={({ vehicleId, startDate, endDate }) =>
            setDialog({
              open: true,
              booking: null,
              defaults: {
                vehicleID: vehicleId,
                category: "project",
                startDate,
                endDate,
              },
            })
          }
          headerActions={
            <Button
              size="sm"
              offlineEdit
              onClick={() =>
                setDialog({
                  open: true,
                  booking: null,
                  defaults: {
                    category: "project",
                    startDate: new Date().toISOString().slice(0, 10),
                  },
                })
              }
              data-testid="planning-new-booking"
            >
              <Plus className="h-4 w-4 mr-1" />
              New booking
            </Button>
          }
        />
      )}

      <VehicleBookingDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        booking={dialog.booking}
        defaults={dialog.defaults}
      />
    </div>
  );
};

export default VehiclePlanningPage;
